import { getApiHeaders } from '../utils/apiHeaders';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const TRAVEL_API_PATH = '/api/travel';

export interface FetchHotelsParams {
  // City code (IATA/City code like 'DEL') from Location API value
  city: string;
  checkIn: string;
  checkOut: string;
  rooms: Array<{
    Adults: number;
    Children: number;
    ChildrenAges: number[] | null;
  }>;
  filters?: {
    StarRating?: number;
    MealType?: string;
    HotelName?: string;
    Amenities?: string[];
    Provider?: string;
    [key: string]: any;
  };
  page?: number;
  limit?: number; // alias: pageSize also accepted by API
}

export interface HotelsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface HotelsResponse<T = any> {
  items: T[];
  pagination?: HotelsPagination;
  priceJobId?: string;
  price_job_id?: string;
}

type ApiErrorPayload = {
  error?: string;
  message?: string;
  code?: string;
  requestId?: string;
  request_id?: string;
};

const parseJsonSafe = (text: string) => {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const buildApiError = (
  payload: unknown,
  fallbackMessage: string,
  fallbackStatus?: { status: number; statusText: string }
) => {
  const stringMessage = typeof payload === 'string' ? payload : '';
  const asObj = payload && typeof payload === 'object' ? (payload as ApiErrorPayload) : null;
  const message = asObj?.error || asObj?.message || stringMessage || fallbackMessage;
  const code = asObj?.code;
  const requestId = asObj?.requestId || asObj?.request_id;
  const prefix =
    fallbackStatus && fallbackStatus.status
      ? `[${fallbackStatus.status}${fallbackStatus.statusText ? ` ${fallbackStatus.statusText}` : ''}] `
      : '';
  const suffix = [
    code ? `code=${code}` : '',
    requestId ? `requestId=${requestId}` : '',
  ]
    .filter(Boolean)
    .join(' | ');
  const error = new Error(`${prefix}${message}${suffix ? ` (${suffix})` : ''}`);
  (error as any).code = code;
  (error as any).requestId = requestId;
  return error;
};

const parseResponseBody = async (res: Response) => {
  const text = await res.text();
  const json = parseJsonSafe(text);
  if (json !== null) return json;
  return text;
};

const extractItems = (body: any): any[] => {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== 'object') return [];
  if (Array.isArray(body.items)) return body.items;
  if (Array.isArray(body.data)) return body.data;
  if (body.data && typeof body.data === 'object' && Array.isArray(body.data.items)) {
    return body.data.items;
  }
  if (Array.isArray(body.hotels)) return body.hotels;
  if (body.data && typeof body.data === 'object' && Array.isArray(body.data.hotels)) {
    return body.data.hotels;
  }
  return [];
};

const extractPagination = (body: any): HotelsPagination | undefined => {
  if (!body || typeof body !== 'object') return undefined;
  const direct = body.pagination;
  const nested = body.data?.pagination;
  const candidate = direct || nested;
  if (!candidate || typeof candidate !== 'object') return undefined;
  const page = Number((candidate as any).page);
  const limit = Number((candidate as any).limit);
  const total = Number((candidate as any).total);
  const totalPages = Number((candidate as any).totalPages ?? (candidate as any).total_pages);
  if ([page, limit, total, totalPages].every((n) => Number.isFinite(n))) {
    return { page, limit, total, totalPages };
  }
  return undefined;
};

const extractPriceJobId = (body: any): string | undefined => {
  if (!body || typeof body !== 'object') return undefined;
  const value =
    body.priceJobId ||
    body.price_job_id ||
    body.searchId ||
    body.search_id ||
    body.jobId ||
    body.job_id ||
    body.data?.priceJobId ||
    body.data?.price_job_id ||
    body.data?.searchId ||
    body.data?.search_id;
  if (value === null || value === undefined) return undefined;
  const asText = String(value).trim();
  return asText || undefined;
};

export const fetchHotels = async (params: FetchHotelsParams): Promise<HotelsResponse> => {
  const res = await fetch(`${API_BASE_URL}${TRAVEL_API_PATH}/hotels/search`, {
    method: 'POST',
    headers: getApiHeaders('json'),
    body: JSON.stringify({ ...params, page: params.page ?? 1 }),
  });

  const body = await parseResponseBody(res);
  if (!res.ok) {
    throw buildApiError(body, 'Failed to fetch hotels', {
      status: res.status,
      statusText: res.statusText,
    });
  }

  return {
    items: extractItems(body),
    pagination: extractPagination(body),
    priceJobId: extractPriceJobId(body),
    price_job_id: extractPriceJobId(body),
  };
};

export type HotelPricesResponse = {
  status: 'PENDING' | 'DONE' | 'FAILED' | string;
  prices?: Record<string, any>;
  [key: string]: any;
};

export const fetchHotelPrices = async (priceJobId: string): Promise<HotelPricesResponse> => {
  const res = await fetch(`${API_BASE_URL}${TRAVEL_API_PATH}/hotels/search/${encodeURIComponent(priceJobId)}/prices`, {
    method: 'GET',
    headers: getApiHeaders('json'),
  });

  const body = await parseResponseBody(res);
  if (!res.ok) {
    throw buildApiError(body, 'Failed to fetch hotel prices', {
      status: res.status,
      statusText: res.statusText,
    });
  }

  if (body && typeof body === 'object') {
    return body as HotelPricesResponse;
  }
  return { status: 'FAILED', prices: {} };
};

export interface HotelPrebookPayload {
  offerId: string;
  provider?: string;
  hotelRef?: string;
  [key: string]: unknown;
}

export interface HotelBookPayload {
  [key: string]: unknown;
}

export interface HotelBookResponse {
  status?: string;
  bookingMode?: string;
  booking_mode?: string;
  isManualConfirmation?: boolean;
  [key: string]: unknown;
}

export interface PriceHotelByCodePayload {
  hotelCode?: string;
  hotelRef?: string;
  checkIn: string;
  checkOut: string;
  rooms: Array<{
    Adults: number;
    Children: number;
    ChildrenAges: number[] | null;
  }>;
  roomCount?: number;
}

export interface PriceHotelByCodeResult {
  price: number | null;
  currency?: string;
  displayPrice?: number;
  displayCurrency?: string;
  raw: unknown;
}

const extractFirstPriceResult = (body: any): any => {
  if (Array.isArray(body)) return body[0];
  if (!body || typeof body !== 'object') return body;
  if (Array.isArray(body.items)) return body.items[0];
  if (body.data && typeof body.data === 'object' && Array.isArray(body.data.items)) {
    return body.data.items[0];
  }
  return body;
};

const extractNumericPrice = (value: any): number | null => {
  const candidates = [
    value?.price,
    value?.display_price,
    value?.displayPrice,
    value?.amount,
    value?.price?.amount,
    value?.offeredPrice,
  ];
  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

export const priceHotelByCode = async (payload: PriceHotelByCodePayload): Promise<PriceHotelByCodeResult> => {
  const res = await fetch(`${API_BASE_URL}${TRAVEL_API_PATH}/hotels/price`, {
    method: 'POST',
    headers: getApiHeaders('json'),
    body: JSON.stringify({
      ...(payload.hotelRef ? { hotelRef: payload.hotelRef } : {}),
      ...(!payload.hotelRef && payload.hotelCode ? { hotelCode: payload.hotelCode } : {}),
      checkIn: payload.checkIn,
      checkOut: payload.checkOut,
      rooms: payload.rooms,
      ...(payload.roomCount ? { roomCount: payload.roomCount } : {}),
    }),
  });

  const body = await parseResponseBody(res);
  if (!res.ok) {
    throw buildApiError(body, 'Failed to price hotel', {
      status: res.status,
      statusText: res.statusText,
    });
  }

  const item = extractFirstPriceResult(body);
  const price = extractNumericPrice(item);
  const displayPriceCandidate = Number(item?.display_price ?? item?.displayPrice);
  const nestedCurrency = item?.price && typeof item.price === 'object' ? item.price.currency : undefined;

  return {
    price,
    currency: item?.currency || nestedCurrency || undefined,
    displayPrice: Number.isFinite(displayPriceCandidate) ? displayPriceCandidate : undefined,
    displayCurrency: item?.display_currency || item?.displayCurrency || undefined,
    raw: body,
  };
};

export const prebookHotel = async (payload: HotelPrebookPayload) => {
  const requestPayload = {
    offerId: payload.offerId,
    ...(payload.provider ? { provider: payload.provider } : {}),
    ...(payload.hotelRef ? { hotelRef: payload.hotelRef } : {}),
  };

  const res = await fetch(`${API_BASE_URL}${TRAVEL_API_PATH}/hotels/prebook`, {
    method: 'POST',
    headers: getApiHeaders('json'),
    body: JSON.stringify(requestPayload),
  });

  const body = await parseResponseBody(res);
  if (!res.ok) {
    throw buildApiError(body, 'Failed to prebook hotel', {
      status: res.status,
      statusText: res.statusText,
    });
  }

  return body;
};

export const bookHotel = async (payload: HotelBookPayload): Promise<HotelBookResponse> => {
  const res = await fetch(`${API_BASE_URL}${TRAVEL_API_PATH}/hotels/book`, {
    method: 'POST',
    headers: getApiHeaders('json'),
    body: JSON.stringify(payload || {}),
  });

  const body = await parseResponseBody(res);
  if (!res.ok) {
    throw buildApiError(body, 'Failed to book hotel', {
      status: res.status,
      statusText: res.statusText,
    });
  }

  if (!body || typeof body !== 'object') {
    return {};
  }

  const status = String((body as any).status || '').toUpperCase();
  const bookingMode = String((body as any).bookingMode || (body as any).booking_mode || '').toUpperCase();
  const isManualConfirmation =
    status === 'PENDING_CONFIRMATION' || bookingMode === 'MANUAL';

  return {
    ...(body as Record<string, unknown>),
    status: (body as any).status,
    bookingMode: (body as any).bookingMode || (body as any).booking_mode,
    booking_mode: (body as any).booking_mode || (body as any).bookingMode,
    isManualConfirmation,
  };
};
